import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PrePracticeScreen from 'Components/ExerciseSection/PrePracticeScreen';
import type { RootState } from 'Reducers';
import { hydrateRequested, selectExerciseCells } from 'Reducers/exerciseProgressReducer';
import { deriveTierProgress } from './deriveTierProgress';
import { readModelFromContent, SCALES_EXERCISE_SLUG } from './exerciseReadModel';
import { useExerciseContent } from './useExerciseContent';
import './ExercisePrePractice.css';

/**
 * Route container for the Exercises section pre-practice screen (MUS-312 / MUS-480).
 *
 * The exercise STRUCTURE (its id, levels, tiers and cells) is the server's, read by
 * slug (`GET /exercises/:slug`); the grip is the live per-cell frontier slice
 * (MUS-318), hydrated once the exercise's id is known and read through the slice's thin
 * selector, with the per-tier grip derived by `deriveTierProgress` (the #117.5
 * waypoint-domination read). The screen is props-typed on the read model and does not
 * know where it comes from.
 *
 * The route is registered flag-gated on USE_EXERCISES in `App.tsx`, so this mounts
 * only where the section is enabled (never in production, where the flag is
 * force-off), and the hydrate saga is itself dark behind the same flag. The
 * internal PageViewEvent for '/exercises' is emitted by `App.tsx`'s per-navigation
 * effect (MUS-309); nothing is dispatched here for analytics.
 */
const ExercisePrePractice: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { status, content } = useExerciseContent(SCALES_EXERCISE_SLUG);
  const readModel = React.useMemo(
    () => (content ? readModelFromContent(content) : null),
    [content],
  );
  const exerciseId = readModel?.exerciseId ?? null;

  // Tap-to-play (MUS-481): entering the real play route for the tapped level/tier/key.
  // The play screen resolves the tapped key (or the level's first cell) at the tier's
  // waypoint; a missing tier defaults to the level's first tier.
  const onSelectLevel = React.useCallback(
    (levelNumber: number, tierNumber?: number, tonic?: string) => {
      const tierSegment = tierNumber ?? 1;
      const tonicSegment = tonic ? `/${encodeURIComponent(tonic)}` : '';
      navigate(`/exercises/play/${levelNumber}/${tierSegment}${tonicSegment}`);
    },
    [navigate],
  );

  // Hydrate the per-cell frontier once the exercise's id is known (server-authoritative;
  // App.tsx does NOT dispatch a hydrate for '/exercises', so the container owns it).
  // Dark behind USE_EXERCISES via the saga's own guard.
  React.useEffect(() => {
    if (exerciseId !== null) dispatch(hydrateRequested({ exerciseId }));
  }, [dispatch, exerciseId]);

  const cellsByKey = useSelector((s: RootState) => selectExerciseCells(s, exerciseId ?? 0));

  // Overlay live grip on the structure. Memoized on the hydrated cells so a re-derive
  // happens only when the frontier actually changes.
  const progress = React.useMemo(
    () => (readModel ? deriveTierProgress(readModel, cellsByKey) : undefined),
    [readModel, cellsByKey],
  );

  if (!readModel) {
    return (
      <div className="exercise-prepractice-page">
        {status === 'failed' && (
          <p className="epp-load-failed" role="alert">
            The exercises could not be loaded. Check your connection and try again.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="exercise-prepractice-page">
      <PrePracticeScreen readModel={{ ...readModel, progress }} onSelectLevel={onSelectLevel} />
    </div>
  );
};

export default ExercisePrePractice;
